import { useState } from 'react';
import { useAccountContext, useAccountContextDispatch } from '../context/AccountContext';
import { useNavigate } from 'react-router-dom';
import { signin, register as registerApi, getGoogleAuth, getTokens, createLongLifeAPIKey, setLongLifeAPIKey, verifyAndLoginWithApiKey } from '../api-helper';
import { db } from '../db';
import electronBridge from '../utils/electronBridge';
import Swal from 'sweetalert2';

/**
 * Custom hook for handling user login functionality
 * Extracted from Profile.jsx to be reusable across components
 */
export const useLogin = () => {
    const [emailAddress, setEmailAddress] = useState("");
    const [password, setPassword] = useState("");
    const [passwordVisibility, setPasswordVisibility] = useState(false);
    const [logloading, setLogloading] = useState(false);
    const [error, setError] = useState("");

    // Registration state
    const [registerEmail, setRegisterEmail] = useState("");
    const [registerPassword, setRegisterPassword] = useState("");
    const [registerConfirmPassword, setRegisterConfirmPassword] = useState("");
    const [registerPasswordVisibility, setRegisterPasswordVisibility] = useState(false);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerError, setRegisterError] = useState("");

    // Google Sign In state
    const [googleSignInLoading, setGoogleSignInLoading] = useState(false);

    // API Key login state
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [apiKeyEmailInput, setApiKeyEmailInput] = useState('');
    const [apiKeyLoading, setApiKeyLoading] = useState(false);
    const [apiKeyError, setApiKeyError] = useState('');

    const accountContext = useAccountContext();
    const accountContextDispatch = useAccountContextDispatch();
    const navigate = useNavigate();

    /**
     * Get the login method - copied from Profile.jsx
     */
    const getMethod = async () => {
        let ip = '';
        try {
            const response = await fetch('https://api.ipify.org');
            ip = await response.text();
            console.log(ip);
        } catch (error) {
            console.error(error);
        }

        const fullURL = window.location.href;

        return signin(emailAddress, password, ip, fullURL);
    };

    /**
     * Handle successful login - copied from Profile.jsx
     */
    const handleLoginSuccess = async (data, shouldNavigate = true) => {
        if (data.message) {
            setError(data.message);
            setLogloading(false);
            return false;
        } else {
            console.log("User login details: ", data);

            // Clear any leftover API key account flags from a previous API key login
            window.sessionStorage.removeItem('isApiKeyAccount');

            // Store account in localStorage
            localStorage.setItem("account", JSON.stringify(data.account));

            // Update context
            accountContextDispatch({type: 'login', signin: data.account, passSecret: password});

            // Store session keys in both sessionStorage and localStorage for persistence
            if (data.api_key) {
                window.sessionStorage.setItem("apiKey", data.api_key);
                window.localStorage.setItem("apiKey", data.api_key);
            }

            // Store expiry BEFORE creating long life key (required for getAuthorizationToken to work)
            if (data.api_key_expiry) {
                window.sessionStorage.setItem("apiKeyExpiry", data.api_key_expiry);
                window.localStorage.setItem("apiKeyExpiry", data.api_key_expiry);
            }

            // Create long life API key for worker applications (after both apiKey and expiry are stored)
            if (data.api_key) {
                try {
                    console.log('[useLogin] Creating long life API key for accountNo:', data.account.accountNo);
                    const longLifeKeyResponse = await createLongLifeAPIKey(data.account.accountNo);
                    console.log('[useLogin] Long life API key response:', longLifeKeyResponse);
                    if (longLifeKeyResponse && !longLifeKeyResponse.error && longLifeKeyResponse.api_key) {
                        await setLongLifeAPIKey(longLifeKeyResponse.api_key, longLifeKeyResponse.api_key_expiry);
                        console.log('[useLogin] Long life API key created and stored');
                    } else {
                        console.warn('[useLogin] Long life API key response invalid or error:', longLifeKeyResponse);
                    }
                } catch (error) {
                    console.error('[useLogin] Failed to create long life API key:', error);
                }
            }

            // Fetch user tokens
            try {
                await getTokens(data.account.accountNo).then(tokenData => {
                    accountContextDispatch({ type: "tokens", tokens: tokenData.credits });
                });
            } catch (error) {
                console.error("Error fetching tokens during login: ", error);
                // Don't set tokens to 0 on error - keep undefined to prevent false low credit warnings
                console.log('[LOGIN_TOKEN_FETCH] Leaving tokens undefined to prevent false credit warnings');
            }

            // Navigate if requested
            if (shouldNavigate) {
                navigate('/');
            }

            setLogloading(false);
            setError("");
            return true;
        }
    };

    /**
     * Main login function - copied from Profile.jsx
     */
    const login = async (onSuccess) => {
        setLogloading(true);
        setError("");

        try {
            const data = await getMethod();

            if (data.status === "unverified") {
                setError(data.message || "Your email address has not been verified yet - please click the link sent to you to verify before you login");
                setLogloading(false);

                // Show a more prominent message using Swal for better visibility
                Swal.fire({
                    title: "Email Verification Required",
                    text: "Please check your email and click the verification link we sent you before logging in.",
                    icon: "info",
                    confirmButtonText: "OK",
                    confirmButtonColor: "#FFB02E"
                });
                return false;
            } else {
                const success = await handleLoginSuccess(data, false); // Don't auto-navigate
                if (success && onSuccess) {
                    onSuccess(data);
                }
                return success;
            }
        } catch (error) {
            console.error("Error during login:", error);
            setLogloading(false);
            setError('Login failed. Please check your credentials and try again.');
            return false;
        }
    };

    /**
     * Handle user registration
     */
    const register = async (onSuccess) => {
        // Reset any previous errors
        setRegisterError("");

        // Validate form
        if (!registerEmail) {
            setRegisterError("Email address is required");
            return false;
        }

        if (!registerPassword) {
            setRegisterError("Password is required");
            return false;
        }

        if (registerPassword !== registerConfirmPassword) {
            setRegisterError("Passwords do not match");
            return false;
        }

        setRegisterLoading(true);

        try {
            // Fetch IP address
            let ip_address = '';
            try {
                const ipResponse = await fetch('https://api.ipify.org');
                ip_address = await ipResponse.text();
                console.log('Registration IP:', ip_address);
            } catch (error) {
                console.error('Failed to fetch IP address:', error);
            }

            // Get user agent
            const user_agent = navigator.userAgent;
            console.log('Registration User Agent:', user_agent);

            const response = await registerApi(registerEmail, registerPassword, ip_address, user_agent);

            if (response.message) {
                // Check if this is an error message or a verification notification
                if (response.status === "unverified" || response.message.includes("verify") || response.message.includes("verification")) {
                    // This is a verification notification, not an error
                    setRegisterLoading(false);

                    // Prefill login form with registration details
                    setEmailAddress(registerEmail);
                    setPassword(registerPassword);

                    // Reset registration form
                    setRegisterEmail("");
                    setRegisterPassword("");
                    setRegisterConfirmPassword("");
                    setRegisterError("");

                    // Show verification message
                    Swal.fire({
                        title: "Verify Your Email",
                        text: "We've sent a verification link to your email address. Please check your inbox and click the link to verify your account before logging in.",
                        icon: "info",
                        confirmButtonText: "OK",
                        confirmButtonColor: "#FFB02E"
                    });

                    if (onSuccess) {
                        onSuccess(response);
                    }

                    return true;
                } else {
                    // This is an actual error message
                    setRegisterError(response.message);
                    setRegisterLoading(false);
                    return false;
                }
            } else {
                // Registration successful
                setRegisterLoading(false);

                // Prefill login form with registration details
                setEmailAddress(registerEmail);
                setPassword(registerPassword);

                // Reset registration form
                setRegisterEmail("");
                setRegisterPassword("");
                setRegisterConfirmPassword("");
                setRegisterError("");

                // Show success message
                Swal.fire({
                    title: "Registration Successful",
                    text: "Your account has been created. You can now log in.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });

                if (onSuccess) {
                    onSuccess(response);
                }

                return true;
            }
        } catch (error) {
            console.error("Registration error: ", error);
            setRegisterError("Registration failed. Please try again.");
            setRegisterLoading(false);
            return false;
        }
    };

    const apiKeyLogin = async (onSuccess) => {
        setApiKeyLoading(true);
        setApiKeyError('');

        try {
            const trimmedKey = apiKeyInput.trim();
            const trimmedEmail = apiKeyEmailInput.trim();

            if (!trimmedKey) {
                setApiKeyError('Please enter an API key.');
                setApiKeyLoading(false);
                return false;
            }

            // Validate key against papi endpoint
            const result = await verifyAndLoginWithApiKey(trimmedKey, trimmedEmail);

            if (!result.success) {
                setApiKeyError(result.message || 'Invalid API Key');
                setApiKeyLoading(false);
                return false;
            }

            const accountDetails = {
                accountNo: result.account?.accountNo || trimmedEmail || 'api-key-user',
                alias: result.account?.alias || trimmedEmail || 'API Key User',
                emailHash: trimmedEmail || '',
                accountTypeId: '0',
                isApiKeyAccount: true,
            };

            window.localStorage.setItem('apiKey', trimmedKey);
            window.localStorage.setItem('apiKeyExpiry', '2099-12-31 23:59:59');
            window.sessionStorage.setItem('apiKey', trimmedKey);
            window.sessionStorage.setItem('apiKeyExpiry', '2099-12-31 23:59:59');
            window.sessionStorage.setItem('isApiKeyAccount', 'true');
            if (trimmedEmail) {
                window.localStorage.setItem('apiKeyEmail', trimmedEmail);
            }
            localStorage.setItem('account', JSON.stringify(accountDetails));
            db.account.put({ key: 'accountDetails', value: accountDetails });

            accountContextDispatch({
                type: 'apiKeyLogin',
                accountDetails: accountDetails,
                tokens: 0
            });

            setApiKeyInput('');
            setApiKeyEmailInput('');
            setApiKeyLoading(false);

            if (onSuccess) {
                onSuccess({ success: true, account: accountDetails, credits: 0 });
            }
            return true;
        } catch (err) {
            console.error('[API_KEY_LOGIN] Error:', err);
            setApiKeyError('An unexpected error occurred. Please try again.');
            setApiKeyLoading(false);
            return false;
        }
    };

    /**
     * Handle Google Sign In
     */
    const handleGoogleSignIn = async () => {
        try {
            setGoogleSignInLoading(true);

            // Set authenticating flag to prevent session expired popup during auth process
            accountContextDispatch({type: "setAuthenticating", value: true});

            const data = await getGoogleAuth();
            console.log(data);

            // Use electronBridge which handles both Electron and browser environments
            electronBridge.authenticateWithGoogle(data.authUrl);
        } catch (error) {
            console.error("Google Sign-In error:", error);
            alert("Failed to initiate Google Sign-In. Please try again.");
            // Clear authenticating flag on error
            accountContextDispatch({type: "setAuthenticating", value: false});
        } finally {
            setGoogleSignInLoading(false);
        }
    };

    /**
     * Reset form to initial state
     */
    const resetForm = () => {
        setEmailAddress("");
        setPassword("");
        setPasswordVisibility(false);
        setError("");
        setLogloading(false);

        // Reset register form
        setRegisterEmail("");
        setRegisterPassword("");
        setRegisterConfirmPassword("");
        setRegisterPasswordVisibility(false);
        setRegisterError("");
        setRegisterLoading(false);

        // Reset API key form
        setApiKeyInput('');
        setApiKeyError('');
        setApiKeyLoading(false);
    };

    return {
        // Login form state
        emailAddress,
        setEmailAddress,
        password,
        setPassword,
        passwordVisibility,
        setPasswordVisibility,
        logloading,
        error,
        setError,

        // Register form state
        registerEmail,
        setRegisterEmail,
        registerPassword,
        setRegisterPassword,
        registerConfirmPassword,
        setRegisterConfirmPassword,
        registerPasswordVisibility,
        setRegisterPasswordVisibility,
        registerLoading,
        registerError,

        // Google Sign In state
        googleSignInLoading,

        // API Key login state
        apiKeyInput,
        setApiKeyInput,
        apiKeyEmailInput,
        setApiKeyEmailInput,
        apiKeyLoading,
        apiKeyError,

        // Actions
        login,
        register,
        apiKeyLogin,
        handleGoogleSignIn,
        resetForm,

        // Context
        accountContext
    };
};